import { useState, useEffect, useRef } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import CheckBox from 'components/CheckBox'
import MapPicker from 'components/MapPicker'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import addressValidator from 'validators/addressValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  Row,
  CheckRow,
  AddressContainer,
  Label,
  SubTitle,
  InfoText,
  FormField,
  WarningText,
  LoadingBackdrop,
  Input,
  MaskedInput
} from '../../styles'

export default function Address ({
  business,
  requestConfig,
  onDataUpdated: handleDataUpdated = () => {}
}) {
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [loadingCoords, setLoadingCoords] = useState (false)
  const [addressFound, setAddressFound] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const hasEditPermission = AuthService.hasPermission ('business_config.personal_data', 'edit')
  const coordsFromMap = useRef (false)
  const address = business?.addresses[0]

  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  return (
    <Formik
      validationSchema={addressValidator}
      initialValues={{
        id: address.id,
        route: address.route,
        number: address.number,
        complement: address.complement,
        is_commercial_building: address.is_commercial_building,
        district: address.district,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        latitude: address.latitude,
        longitude: address.longitude
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting (true)
        try {
          const address = values

          await cutileiApi.put (`/businesses/${business.id}`, { address }, requestConfig)

          handleDataUpdated ({ address })
          setSubmitting (false)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {function AddressForm ({
        dirty,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit
      }) {
        const searchAddress = async event => {
          const postal_code = event.target.rawValue

          setFieldValue ('postal_code', postal_code)
          
          if (postal_code.length === 8) {
            setLoadingAddress (true)
            try {
              const response = await viaCepApi.get (`/ws/${postal_code}/json`)
              const { data } = response

              setFieldValue ('route', data.logradouro)
              setFieldValue ('district', data.bairro)
              setFieldValue ('city', data.localidade)
              setFieldValue ('state', data.uf)

              setAddressFound (true)
            } catch (error) {
              logApiError (error)
            } finally {
              setLoadingAddress (false)
            }
          }
        }

        const getCoordinates = async () => {
          try {
            setLoadingCoords (true)

            const { data: coordinates } = await cutileiApi.post ('/coordinates', values)

            setFieldValue ('latitude', coordinates.latitude)
            setFieldValue ('longitude', coordinates.longitude)
          } catch (error) {
            logApiError (error)
          } finally {
            setLoadingCoords (false)
          }
        }

        const handleLocationChange = async location => {
          try {
            setLoadingAddress (true)
            const { latitude, longitude } = location

            const { data: address } = await cutileiApi.get (
              `/coordinates?latitude=${latitude}&longitude=${longitude}`
            )
            
            coordsFromMap.current = true
            setFieldValue ('route', address.route)
            setFieldValue ('number', address.number)
            setFieldValue ('district', address.district)
            setFieldValue ('city', address.city)
            setFieldValue ('state', address.state)
            setFieldValue ('postal_code', address.postal_code)
            setFieldValue ('latitude', latitude)
            setFieldValue ('longitude', longitude)
          } catch (error) {
            logApiError (error)
          } finally {
            setLoadingAddress (false)
          }
        }

        const debounceTimeout = useRef ()
        
        useEffect (() => {
          if (
            addressFound && !coordsFromMap.current && values.route &&
            values.number && values.city && values.state
          ) {
            if (debounceTimeout.current) clearTimeout (debounceTimeout.current)
            debounceTimeout.current = setTimeout (() => {
              getCoordinates ()
              coordsFromMap.current = false
            }, 700)
          }
          return () => clearTimeout (debounceTimeout.current)
        }, [addressFound, values.route, values.number, values.city, values.state])

        return (
          <Form>
            <SubTitle>Endereço</SubTitle>
            <AddressContainer>
              {loadingAddress && (
                <LoadingBackdrop>
                  <Loading/>
                </LoadingBackdrop>
              )}
              <Row>
                <FormField>
                  <MaskedInput
                    name='postal_code'
                    placeholder='CEP'
                    options={{
                      blocks: [5,3],
                      delimiters: ['-'],
                      numericOnly: true
                    }}
                    value={values.postal_code}
                    disabled={!hasEditPermission}
                    onChange={searchAddress}
                    onBlur={handleBlur ('postal_code')}
                  />
                  {touched.postal_code && errors.postal_code && (
                    <WarningText>{errors.postal_code}</WarningText>
                  )}
                </FormField>
                <FormField stretch={2}>
                  <Input placeholder='Rua/avenida' value={values.route} disabled/>
                </FormField>
                <FormField>
                  <Input
                    placeholder='Número'
                    value={values.number}
                    disabled={!hasEditPermission}
                    onChange={handleChange ('number')}
                    onBlur={handleBlur ('number')}
                  />
                  {touched.number && errors.number && (
                    <WarningText>{errors.number}</WarningText>
                  )}
                </FormField>
                <FormField>
                  <Input
                    placeholder='Complemento'
                    value={values.complement}
                    disabled={!hasEditPermission}
                    onChange={handleChange ('complement')}
                    onBlur={handleBlur ('complement')}
                  />
                </FormField>
              </Row>
              <Row>
                <FormField stretch={2}>
                  <Input placeholder='Bairro' value={values.district} disabled/>
                </FormField>
                <FormField>
                  <Input placeholder='Cidade' value={values.city} disabled/>
                </FormField>
                <FormField>
                  <Input placeholder='Estado' value={values.state} disabled/>
                </FormField>
              </Row>
              <FormField>
                <SubTitle style={{marginBottom: 8}}>
                  Seu salão fica em um prédio comercial?
                </SubTitle>
                <Row style={{gap: 15}}>
                  <CheckRow>
                    <CheckBox
                      id='is_commercial_building'
                      value={values.is_commercial_building}
                      disabled={!hasEditPermission}
                      onValueChange={() => setFieldValue ('is_commercial_building', true)}
                    />
                    <Label htmlFor='is_commercial_building'>
                      Sim
                    </Label>
                  </CheckRow>
                  <CheckRow>
                    <CheckBox
                      id='!is_commercial_building'
                      value={!values.is_commercial_building}
                      disabled={!hasEditPermission}
                      onValueChange={() => setFieldValue ('is_commercial_building', false)}
                    />
                    <Label htmlFor='!is_commercial_building'>
                      Não
                    </Label>
                  </CheckRow>
                </Row>
              </FormField>
            </AddressContainer>
            {loadingCoords ? (
              <Row>
                <Loading/>
              </Row>
            ) : values.latitude !== 0 && values.longitude !== 0 && hasEditPermission && (
              <FormField>
                <SubTitle style={{textAlign: 'center', marginBottom: 8}}>
                  {'Mantenha o cursor do mouse pressionado sobre o pino de localização '
                  + 'e o arraste para a localização exata do seu salão, se for necessário'}
                </SubTitle>
                <InfoText style={{marginBottom: 15}}>
                  {'Este ajuste é muito importante para que a Cutilei possa '
                  + 'verificar a localização dos clientes ao validar agendamentos'}
                </InfoText>
                <MapPicker
                  initialCoords={{
                    latitude: values.latitude,
                    longitude: values.longitude
                  }}
                  onLocationSelected={handleLocationChange}
                />
              </FormField>
            )}
            <AlertDialog
              visible={showOkDialog}
              title='Sucesso'
              message='Alterações salvas!'
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
            {hasEditPermission && dirty && (
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={160}
                style={{marginTop: 15}}
              />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
